import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPinIcon, 
  ArrowsRightLeftIcon, 
  CheckIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import UniversityVerifiedBadge from '../university/UniversityVerifiedBadge';
import { formatFeeDisplay } from '../../utils/formatters';
import { getCompare, addToCompare, removeFromCompare } from '../../utils/compare';

const ProgramCard = ({ program }) => {
  const [inCompare, setInCompare] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const uni = program.university || {};
  const isPartner = uni.is_partner === true;
  const uniName = uni.name || 'University';
  const uniLocation = uni.city || uni.location || 'Pakistan';
  const logo = uni.profile_picture || uni.logo;

  const getInitials = (str) => {
    if (!str) return 'UN';
    return str
      .split(' ')
      .filter(w => w.length > 0)
      .slice(0, 2)
      .map(w => w[0].toUpperCase())
      .join('');
  };

  useEffect(() => {
    const checkCompare = () => {
      const list = getCompare();
      const uniqueId = `${program.type || (isPartner ? 'partner' : 'static')}:${program.id}`;
      setInCompare(list.some(item => item.uniqueId === uniqueId || item.id === program.id));
    };

    checkCompare();

    const handleCompareUpdate = () => checkCompare();
    window.addEventListener('compareUpdated', handleCompareUpdate);
    return () => window.removeEventListener('compareUpdated', handleCompareUpdate);
  }, [program.id, program.type, isPartner]);

  const toggleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const type = program.type || (isPartner ? 'partner' : 'static');
    if (inCompare) {
      removeFromCompare(program.id, type);
    } else {
      addToCompare({
        id: program.id,
        program_name: program.name || program.program_name,
        name: program.name || program.program_name,
        degree_type: program.degree_type,
        program_fee: program.fee || program.program_fee,
        fee_type: program.fee_type,
        type: type,
        university: {
          id: uni.id,
          name: uniName,
          slug: uni.slug,
          profile_picture: logo,
          logo: logo,
          is_partner: isPartner,
          location: uniLocation
        }
      });
    }
  };

  const uniUrl = `/university/${uni.slug || uni.id || ''}`;
  const programUrl = program.id ? `${uniUrl}?programId=${program.id}#program-${program.id}` : uniUrl;
  const feeDisplay = formatFeeDisplay(program.fee || program.program_fee, program.fee_type);

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 p-5 sm:p-6 shadow-xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-5">
      {/* Left Column: University Logo & Program Details */}
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <Link to={uniUrl} className="shrink-0 pt-0.5">
          {logo && !logoError ? (
            <img
              src={logo}
              alt={uniName}
              className="w-12 h-12 rounded-xl object-contain border border-slate-100 bg-white shadow-2xs p-1"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 font-bold text-sm flex items-center justify-center shadow-2xs">
              {getInitials(uniName)}
            </div>
          )}
        </Link>

        <div className="min-w-0 flex-1">
          {/* Program Title at the very top */}
          <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug mb-1 break-words">
            <Link to={programUrl}>
              {program.name || program.program_name}
            </Link>
          </h3>

          {/* Institutional subline: Uni Name, Verified Badge, Location */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 font-medium mb-2.5">
            <Link 
              to={uniUrl} 
              className="font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5 transition-colors"
            >
              <span>{uniName}</span>
              <UniversityVerifiedBadge isPartner={isPartner} className="h-4 w-4 shrink-0" />
            </Link>
            <span className="text-slate-300">•</span>
            <div className="inline-flex items-center text-slate-500">
              <MapPinIcon className="h-3.5 w-3.5 mr-1 text-slate-400 shrink-0" />
              <span>{uniLocation}</span>
            </div>
          </div>

          {/* Badges Row: Uniform neutral styling for all tags */}
          <div className="flex flex-wrap items-center gap-1.5">
            {program.degree_type && (
              <span className="text-xs font-medium px-2.5 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200">
                {program.degree_type}
              </span>
            )}
            {program.field_of_study && (
              <span className="text-xs font-medium px-2.5 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200">
                {program.field_of_study}
              </span>
            )}
            {program.duration && (
              <span className="text-xs font-medium px-2.5 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200">
                {program.duration}
              </span>
            )}
            {program.deadline_status === 'Open' && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Admissions Open
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right Column: Fee & Actions Rail */}
      <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-3 shrink-0 border-t md:border-t-0 md:border-l border-slate-100 pt-3 md:pt-0 md:pl-6 min-w-[190px]">
        <div className="text-left md:text-right">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Tuition Fee
          </p>
          <p className="text-base sm:text-lg font-extrabold text-slate-900">
            {feeDisplay}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Compare Button */}
          <button
            type="button"
            onClick={toggleCompare}
            title={inCompare ? "Remove from comparison" : "Add to comparison"}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-all border ${
              inCompare
                ? 'bg-primary-50 text-primary-700 border-primary-300 shadow-2xs'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900 shadow-2xs'
            }`}
          >
            {inCompare ? (
              <>
                <CheckIcon className="h-4 w-4 text-primary-600" />
                <span>Added</span>
              </>
            ) : (
              <>
                <ArrowsRightLeftIcon className="h-4 w-4 text-slate-400" />
                <span>Compare</span>
              </>
            )}
          </button>

          {/* View Details */}
          <Link
            to={programUrl}
            className="inline-flex items-center gap-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold text-xs px-3.5 py-2 rounded-xl transition-colors shadow-sm"
          >
            <span>Details</span>
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-80" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;
